import React, { useState } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Icon from './Icon';
import SectionTitle from './SectionTitle';

const FAQSection = styled.section`
  padding: 6rem 0;
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const FAQDescription = styled.p`
  text-align: center;
  color: #666;
  font-size: 1.2rem;
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
`;

const FAQItem = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #eee;

  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const Question = styled.button`
  width: 100%;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  text-align: left;
  transition: all 0.3s ease;

  &:hover {
    color: #1c9dbd;
  }

  svg {
    color: #1c9dbd;
    transition: transform 0.3s ease;
  }
`;

const Answer = styled.div<{ isOpen: boolean }>`
  padding: ${({ isOpen }) => (isOpen ? '1.5rem' : '0')};
  max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: all 0.3s ease;
  color: #666;
  line-height: 1.6;
  border-top: ${({ isOpen }) => (isOpen ? '1px solid #eee' : 'none')};
  font-size: 1.1rem;
`;

const PhoneLink = styled.a`
  color: #1c9dbd;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s ease;

  &:hover {
    color: #ff9d5c;
  }
`;

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "Quais são os horários de funcionamento da clínica?",
      answer: "Atendemos de segunda a sexta-feira, das 8h às 18h, e aos sábados das 8h às 12h. Para emergências, entre em contato para confirmar se estamos de plantão."
    },
    {
      question: "Preciso agendar com antecedência para consultas?",
      answer: "Recomendamos o agendamento prévio para garantir o melhor horário para você e seu pet, evitando esperas. No entanto, também realizamos atendimentos sem agendamento, de acordo com a disponibilidade da agenda. Em casos de urgência, entre em contato imediatamente para avaliarmos a melhor forma de atendimento."
    },
    {
      question: "Quais formas de pagamento são aceitas?",
      answer: "Aceitamos pagamentos em dinheiro, cartões de crédito e débito, PIX e transferência bancária. Nossa equipe está à disposição para esclarecer qualquer dúvida sobre as formas de pagamento disponíveis."
    },
    {
      question: "Como devo preparar meu pet para a consulta?",
      answer: "Recomendamos trazer seu pet com coleira ou guia, carteira de vacinação atualizada e, se possível, histórico médico anterior. Para exames específicos, pode ser necessário jejum - nossa equipe irá orientá-lo sobre os preparativos necessários."
    },
    {
      question: "Como funciona o atendimento de emergência?",
      answer: (
        <>
          Em caso de emergência, entre em contato pelo telefone{' '}
          <PhoneLink href="tel:77999441369">(77) 99944-1369</PhoneLink>{' '}
          para confirmar se estamos de plantão. Quando estivermos de plantão, nossa equipe estará pronta para atender seu pet imediatamente, com toda a estrutura necessária para cuidados emergenciais.
        </>
      )
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <FAQSection id="faq">
      <Container>
        <SectionTitle>Perguntas Frequentes</SectionTitle>
        <FAQDescription>
          Tire suas dúvidas sobre nossos serviços e procedimentos. Se não encontrar a resposta que procura, entre em contato conosco.
        </FAQDescription>
        <FAQList>
          {faqs.map((faq, index) => (
            <FAQItem key={index}>
              <Question onClick={() => toggleFAQ(index)}>
                {faq.question}
                {openIndex === index ? <Icon icon={FaChevronUp} size={20} /> : <Icon icon={FaChevronDown} size={20} />}
              </Question>
              <Answer isOpen={openIndex === index}>
                {faq.answer}
              </Answer>
            </FAQItem>
          ))}
        </FAQList>
      </Container>
    </FAQSection>
  );
};

export default FAQ; 